/**
 * Question Mapper Utility
 * Maps originalQuestion text to actual question numbers in the survey
 * Note: CSV columns 1-5 are typically metadata (Id, Start time, Completion time, Email, Name)
 * Real questions start from column 6, so we map to Q1, Q2, Q3...
 */

export interface QuestionMapping {
  [key: string]: number;
}

/**
 * Common question patterns that typically map to certain positions
 * This is used as a fallback when specific mapping is not available
 */
const COMMON_QUESTION_PATTERNS: QuestionMapping = {
  // Registration/Attendance (usually early questions)
  'have you registered': 1,
  'did you register': 1,
  'are you registered': 1,
  'will you register': 1,
  'reason you did not register': 2,
  'why did you not register': 2,
  
  // Satisfaction/Rating (usually Q3-Q5)
  'rate this': 3,
  'rate the': 3,
  'rate your': 3,
  'satisfaction': 3,
  'how satisfied': 3,
  'overall rating': 3,
  'overall satisfaction': 3,
  'how would you rate': 3,
  
  // Preference/Choice questions
  'which option': 4,
  'what option': 4,
  'which one': 4,
  'what type': 4,
  'which type': 4,
  'select one': 4,
  
  // Attendance/Participation
  'did you attend': 5,
  'have you attended': 5,
  'will you attend': 5,
  'did you participate': 5,
  
  // Follow-up reasons
  'if you did not': 6,
  'why did you not': 6,
  'reason for not': 6,
  
  // Open feedback
  'what do you think': 7,
  'what aspects': 7,
  'which aspects': 7,
  'what did you like': 7,
  'feedback': 7,
  'comments': 8,
  'suggestions': 8,
  'any other': 8,
  'additional comments': 8,
  
  // Future preferences
  'what would you prefer': 9,
  'what kind of': 9,
  'what type of': 9,
  'prefer for next': 9,
  'would you like': 9,
  
  // Specific details
  'please write': 10,
  'please specify': 10,
  'please list': 10,
  
  // Timing preferences
  'when would': 11,
  'what time': 11,
  'time you prefer': 11,
  'timing': 11,
};

/**
 * Intelligent question number extraction
 * Tries multiple strategies to find the question number
 */
export function getQuestionNumber(
  questionText: string,
  customMap?: QuestionMapping
): number | null {
  if (!questionText) return null;
  
  const lowerText = questionText.toLowerCase();
  
  // Strategy 1: Use custom mapping if provided
  if (customMap) {
    for (const [key, num] of Object.entries(customMap)) {
      if (lowerText.includes(key.toLowerCase())) {
        return num;
      }
    }
  }
  
  // Strategy 2: Look for explicit question numbers in the text
  // e.g., "Question 5:", "Q3:", "3.", "(Question 7)"
  const explicitPatterns = [
    /question\s*(\d+)/i,
    /q\s*(\d+)/i,
    /^(\d+)\./,
    /\(question\s*(\d+)\)/i,
  ];
  
  for (const pattern of explicitPatterns) {
    const match = questionText.match(pattern);
    if (match && match[1]) {
      const num = parseInt(match[1]);
      if (num > 0 && num < 100) { // Reasonable question number
        return num;
      }
    }
  }
  
  // Strategy 3: Use common question patterns with fuzzy matching
  // Sort by pattern length (longer patterns first for better matching)
  const sortedPatterns = Object.entries(COMMON_QUESTION_PATTERNS)
    .sort((a, b) => b[0].length - a[0].length);
  
  for (const [key, num] of sortedPatterns) {
    // Check if the pattern appears as a substring
    if (lowerText.includes(key.toLowerCase())) {
      return num;
    }
  }
  
  // Strategy 4: Enhanced keyword matching for common question themes
  // Match key concepts even if wording differs slightly
  const keywordMatches: Array<{ keywords: string[]; questionNum: number }> = [
    // Renewable Energy survey patterns (more specific)
    { keywords: ['main reason.*renewable', 'reasons for using.*renewable'], questionNum: 9 },
    { keywords: ['barrier.*renewable', 'wider adoption.*renewable', 'obstacle.*renewable'], questionNum: 12 },
    { keywords: ['additional comment.*renewable', 'experience.*renewable'], questionNum: 13 },
    { keywords: ['types of renewable', 'which.*renewable.*use'], questionNum: 7 },
    { keywords: ['satisfied.*current.*renewable', 'satisfied.*renewable.*source'], questionNum: 8 },
    { keywords: ['government.*involvement.*renewable', 'rate.*government.*renewable'], questionNum: 10 },
    { keywords: ['expecting.*more.*renewable', 'next.*years.*renewable'], questionNum: 11 },
    
    // App feedback survey patterns (more specific)
    { keywords: ['satisfied.*app.*interface', 'interface.*satisfaction'], questionNum: 12 },
    { keywords: ['satisfied.*app.*performance', 'performance.*satisfaction'], questionNum: 13 },
    { keywords: ['like best.*app', 'what.*like.*about.*app'], questionNum: 9 },
    { keywords: ['dislike.*most.*app', 'what.*dislike.*app'], questionNum: 10 },
    { keywords: ['comments.*suggestions', 'any.*comments.*suggestions'], questionNum: 16 },
  ];
  
  for (const { keywords, questionNum } of keywordMatches) {
    for (const keyword of keywords) {
      const regex = new RegExp(keyword, 'i');
      if (regex.test(lowerText)) {
        return questionNum;
      }
    }
  }
  
  // Strategy 5: If text is very short (< 50 chars), might be a simple question identifier
  // Return null to indicate we couldn't determine the number
  return null;
}

/**
 * Parse multiple questions from a combined originalQuestion string
 * Handles cross-question analysis (e.g., "Q1: Question 1 text + Q2: Question 2 text")
 * @param originalQuestion - Combined question string
 * @param customMap - Optional custom question mapping
 * @returns Array of question numbers
 */
export function parseQuestionNumbers(
  originalQuestion: string | undefined,
  customMap?: QuestionMapping
): number[] {
  if (!originalQuestion) return [];
  
  // Strategy 1: Look for "Qx:" prefix pattern (new format)
  // Matches: "Q1:", "Q12:", "Q 3:", etc.
  const qPrefixPattern = /Q\s*(\d+)\s*:/gi;
  const qPrefixMatches = [...originalQuestion.matchAll(qPrefixPattern)];
  if (qPrefixMatches.length > 0) {
    return qPrefixMatches
      .map(match => parseInt(match[1]))
      .filter(n => n > 0 && n < 100);
  }
  
  // Strategy 2: Split on " + " (space-plus-space) for cross-question analysis
  const questions = originalQuestion.includes(' + ')
    ? originalQuestion.split(' + ').map(q => q.trim())
    : [originalQuestion.trim()];
  
  return questions
    .map(q => getQuestionNumber(q, customMap))
    .filter((n): n is number => n !== null);
}

/**
 * Extract clean question texts for display (strips Qx: prefix)
 * @param originalQuestion - Combined question string
 * @returns Array of clean question texts
 */
export function extractQuestionTexts(
  originalQuestion: string | undefined
): string[] {
  if (!originalQuestion) return [];
  
  // Split on " + " (space-plus-space) for cross-question analysis
  const parts = originalQuestion.includes(' + ')
    ? originalQuestion.split(' + ').map(q => q.trim())
    : [originalQuestion.trim()];
  
  // Remove "Qx:" prefix from each part if present
  return parts.map(part => {
    // Match and remove "Q1:", "Q 2:", etc.
    return part.replace(/^Q\s*\d+\s*:\s*/i, '').trim();
  });
}
